import { ADD_TO_BAG_TEXT } from '@nykaa/product-card-shared/constant/addToCart';
import { getPrimaryCategories } from '@nykaa/product-card-shared/utils';
import getCohortSaleData from '@nykaa/listing-shared/transformer/differentialPricing';
import { getRoundedDiscount } from 'shared/helpers/utils/price';
import {
  EarlyAccessData,
  RawEarlyAccessData,
  SizeData,
  UnitProps,
  Variant,
} from 'store/product/types';
import { MediaTypes } from 'shared/constants/productPage';
import { transformPriceRevealData } from 'helpers/utils/store/PriceReveal';

import {
  Offer,
  Product,
  BrandAuth,
  CategoryLevels,
  CategoryLevelsMap,
  ReviewData,
  RawReviewData,
  ProductSizeData,
  ReviewSplitUp,
  RawMediaItem,
  ManufactureRawData,
  ManufactureData,
} from './types';

import {
  stripScriptTags,
  removeTabCharsFromString,
  removeNewLineCharsFromString,
} from './helpers';
import { isValidObject } from '@nykaa/utils/validations';
/**
 * @name getProductsCategoryLevels
 * @param categoryLevelsMap
 * @description we get a category level map from the backend and is of type
 *  CategoryLevelsMap = {12: "0", 15: "1", 194: "0", 249: "2", 1101: "1", 1127: "2", 2889: "1", 3945: "3", 5926: "0"}
 * * This Map has categories and their corresponding category Levels
 * ? For no known reason we pick the last category in the map for each level.
 */
const getProductsCategoryLevels = (categoryLevelsMap: CategoryLevelsMap) => {
  const transformedCategoryMap: CategoryLevels = {};
  if (!categoryLevelsMap) {
    return transformedCategoryMap;
  }

  const categories: string[] = Object.keys(categoryLevelsMap);
  categories.forEach((category: string) => {
    const level: number = categoryLevelsMap[category];
    if (!transformedCategoryMap[level] && level < 3) {
      transformedCategoryMap[level] = category;
    }
  });

  return transformedCategoryMap;
};

const transformMedia = (media: RawMediaItem[] = []) => {
  if (!media || !Array.isArray(media)) {
    return [];
  }

  const transformRegEx = /tr:.*?\//;

  return media.map((item, index) => {
    const { type, url } = item;
    if (type === MediaTypes.IMAGE) {
      return {
        url: url.replace(transformRegEx, ''),
        mediaType: type,
      };
    }
    if (type === MediaTypes.VIDEO) {
      item.mediaType = MediaTypes.YOUTUBE_VIDEO;
    }
    item.id = index;
    return item;
  });
};

const transformCarousel = (media: RawMediaItem[] = []) => {
  if (!media || !Array.isArray(media)) {
    return [];
  }

  const transformRegEx = /tr:.*?\//;

  return media.map((item, index) => {
    const { mediaType, url, webUrl, videoId } = item;
    if (mediaType === MediaTypes.IMAGE) {
      return {
        url: url.replace(transformRegEx, ''),
        mediaType,
        id: `${index}-main_image`,
      };
    }
    if (mediaType === MediaTypes.VIDEO) {
      item.url = webUrl;
      item.id = videoId;
    }

    return item;
  });
};

export const transformVariantSizeData = (sizeData: SizeData) => {
  let updatedSizeData = sizeData;
  if (updatedSizeData?.label) {
    delete updatedSizeData.label;
  }
  return updatedSizeData;
};

export const transformManufactureData = (
  manufacturers: ManufactureRawData[]
) => {
  if (!Array.isArray(manufacturers)) {
    return [];
  }
  const transformedManufacturers: ManufactureData[] = [];
  manufacturers.forEach((manufacture: ManufactureRawData) => {
    transformedManufacturers.push({
      originOfCountryName: manufacture?.country_of_origin,
      manufacturerName: manufacture?.manufacturer_name,
      manufacturerAddress: manufacture?.manufacturer_address,
      packerName: manufacture?.packer_name,
      packerAddress: manufacture?.packer_address,
      importerName: manufacture?.importer_name,
      importerAddress: manufacture?.importer_address,
      manufacturerNameLabel: manufacture?.manufacturer_name_label,
      manufacturerAddressLabel: manufacture?.manufacturer_address_label,
      packerNameLabel: manufacture?.packer_name_label,
      packerAddressLabel: manufacture?.packer_address_label,
      importerNameLabel: manufacture?.importer_name_label,
      importerAddressLabel: manufacture?.importer_address_label,
      countryOfOriginLabel: manufacture?.country_of_origin_label,
    });
  });
  return transformedManufacturers;
};
export const transformEarlyAccessData = (
  earlyAccessData: RawEarlyAccessData
): EarlyAccessData | null => {
  if (!isValidObject(earlyAccessData)) {
    return null;
  }
  return {
    disablePriveProduct: earlyAccessData.disable_prive_product,
    priveText: earlyAccessData.prive_text,
    textColor: earlyAccessData.text_color,
    gradientColor: earlyAccessData.gradient_color,
  };
};

export const transformVariants = (
  variants: any = [],
  tagsAssets: any
): Variant[] => {
  if (!variants || !Array.isArray(variants)) {
    return [];
  }

  const transformedVariants: Variant[] = [];
  variants.forEach((variant) => {
    const { id: childId } = variant;
    const secondaryTagUrl = tagsAssets?.[variant?.secondary_tag] || '';
    const secondartTagV2Url = tagsAssets?.[variant?.secondary_tag_v2] || '';
    transformedVariants.push({
      brandName: variant.brand_name || '',
      buttonText: variant.button_text,
      newButtonText: variant.button_text_new || ADD_TO_BAG_TEXT,
      childId,
      discount: getRoundedDiscount(variant.discount),
      featured: false, // ! FIXME: confirm featured flag for variant Object
      isBestSeller: variant.is_bestseller,
      inStock: variant.gludo_stock,
      media: transformMedia(variant?.media) || [], // TODO: transform media,
      carousel: transformCarousel(variant?.carousel) || [],
      relatedVideo: transformCarousel(variant?.related_video) || [],
      mrp: variant.price,
      name: variant.name,
      offerPrice: variant.final_price,
      packSize: variant.pack_size,
      shadeImage: variant.shade_background_url,
      quantity: variant.quantity,
      multiMrp: variant.multi_mrp || {},
      sku: variant.sku,
      slug: variant.slug,
      productType: variant.type,
      variantName: variant.option_name,
      expiry: variant.expdt,
      returnAvailable: variant.return_available,
      messageOnReturn: variant.message_on_return,
      onlyWishlistButton: variant.show_wishlist_button,
      imageUrl: variant.image_url,
      sizeData: transformVariantSizeData(variant?.size_data) || {},
      title: variant.name,
      categoryLevels: getProductsCategoryLevels(variant.category_levels),
      manufacture: transformManufactureData(variant.manufacture) || [],
      primaryCategories: getPrimaryCategories(variant?.primary_categories),
      isLux: Number(variant.is_lux) || 0,
      prData: transformPriceRevealData(
        variant?.pr_data,
        variant?.sale_template
      ),
      isPrEnabled: variant?.pr_enabled_raw,
      isEarlyAccess: variant?.early_access,
      isFlashSale: variant?.flash_sale,
      productTitle: variant?.product_title,
      cohortSaleTemplate: getCohortSaleData(variant?.cohort_sale_template),
      earlyAccessData:
        transformEarlyAccessData(variant?.early_access_data) || {},
      secondaryTag: secondaryTagUrl,
      secondaryTagV2: secondartTagV2Url,
    });
  });

  return transformedVariants;
};
export const transformProductOffers = (offers: object[] = []): Offer[] =>
  offers?.map((offer: any) => ({
    id: offer.offer_id || offer.offerId,
    title: offer.title,
    description: offer.description,
    isViewProducts: offer.isViewProducts,
  }));

export const transformProductOffersV2 = (offers: object[] = []): Offer[] =>
  offers.map((offer: any) => ({
    id: offer.offer_id || offer.offerId,
    title: offer.title,
    description: offer.description,
    isViewProducts: offer.isViewProducts,
    expiryEpoch: offer.expiryEpoch,
    deltaAmount: offer?.deltaAmount,
    deltaQuantity: offer?.deltaQuantity,
    deltaTextModified: offer?.deltaTextModified,
    status: offer.status,
  }));

const transformAndSanitizeDetails = (details = '') => {
  if (details) {
    const regexForImgTag = /<img /g;
    const regexForImgSrc = /<img.*?src="(?<imgUrl>.*?)"/g;
    const optimizedDescription = details.replace(
      regexForImgSrc,
      '<img src="$1?tr=w-400,pr-true" '
    );

    optimizedDescription.replace(regexForImgTag, '<img loading="lazy" ');
    return stripScriptTags(
      removeTabCharsFromString(
        removeNewLineCharsFromString(optimizedDescription)
      )
    );
  }
  return null;
};

const transformBrandAuth = (brandAuth: any): undefined | BrandAuth => {
  const { text, certificate_url: certificate, auth_icon: icon } = brandAuth;

  // ? Pick certificate and text are dynamic to product/brand
  return {
    text,
    icon,
    certificate,
  };
};

export const transformTopReview = (reviews: RawReviewData[]) => {
  const updatedReviews: ReviewData[] = [];
  reviews &&
    reviews.forEach((review) => {
      updatedReviews.push({
        title: review.title,
        description: review.detail,
        name: review.nickname,
        createdOn: review.created_at,
        likeCount: review.likes,
        id: Number(review.review_id),
        variantId: Number(review.childId),
        isLikedByUser: review.isLikedByUser,
        isBuyer: review.is_buyer,
        images: review.images || [],
        profilePic: review.customerProfileUrl,
        createdOnText: review.createdAtText,
        rating: Number(review.value),
      });
    });
  return updatedReviews;
};

export const transformColumns = (columnsData?: UnitProps[]) => {
  let updatedColumns: UnitProps[] = [];
  if (columnsData) {
    columnsData.map((column: UnitProps) => {
      if (column.id !== 'label') {
        updatedColumns.push(column);
      }
    });
  }
  return updatedColumns;
};
export const transformSizeData = (sizeData: ProductSizeData) => ({
  enabled: sizeData?.enabled,
  buttonText: sizeData?.button_text || '',
  unitText: sizeData?.unit_text || '',
  units: sizeData?.units || [],
  columns: transformColumns(sizeData?.columns) || [],
});

export const transformRatingReviewData = (reviewSplitUp: ReviewSplitUp[]) =>
  reviewSplitUp.reverse();

/**
 *
 *
 * @description - transforms product Data
 * This also sets a selected skuId if the one passed as param is available in the product.
 * ! If the skuId passed is not present in the product then it selects the first variant/option returned by the API
 */
export const transformProductData = (productData: any, skuId?: string) => {
  const tagsAssets = productData?.tags_assets || {};
  const secondaryTagUrl = tagsAssets?.[productData?.secondary_tag] || '';
  const secondaryTagV2Url = tagsAssets?.[productData?.secondary_tag_v2] || '';

  const transformedProduct: Product = {
    brandName: productData.brand_name,
    brandAuth: transformBrandAuth(productData.brand_auth),
    buttonText: productData.button_text,
    newButtonText: productData.button_text_new || ADD_TO_BAG_TEXT,
    canSubscribe: productData.can_subscribe,
    categoryLevels: getProductsCategoryLevels(productData.category_levels),
    description: productData.description,
    discount: getRoundedDiscount(productData.discount),
    id: productData.id,
    isLux: Number(productData.is_lux) || 0,
    isKitCombo: productData.is_kit_combo,
    inStock: productData.gludo_stock,
    metaDescription: productData.meta_description,
    metaKeywords: productData.meta_keywords,
    metaTitle: productData.meta_title,
    mrp: productData.price,
    name: productData.name,
    multiMrp: productData.multi_mrp,
    offerPrice: productData.final_price,
    offers: transformProductOffers(productData.offers), // TODO: transformOffers
    variants: transformVariants(productData.options, tagsAssets),
    parentId: productData.parent_id,
    packSize: productData.pack_size,
    parentMedia: transformMedia(productData?.media) || [], // TODO: transform media
    parentCarousel: transformCarousel(productData?.carousel) || [],
    parentRelatedVideo: transformCarousel(productData?.related_video) || [],
    primaryCategories: getPrimaryCategories(productData.primary_categories),
    productType: productData.type,
    quantity: productData.quantity,
    rating: productData.rating,
    ratingCount: productData.rating_count,
    returnMessage: productData.message_on_return,
    reviewCount: productData.review_count,
    sku: productData.sku,
    slug: productData.slug,
    variantType: productData.configurable_type,
    ingredients: transformAndSanitizeDetails(productData.ingredients),
    howToUse: transformAndSanitizeDetails(productData.use),
    originOfCountryName: productData.origin_of_country_name,
    countryOfManufacture: productData.country_of_manufacture,
    manufacturerName: productData.manufacturer_name,
    manufacturerAddress: productData.manufacturer_address,
    manufacture: transformManufactureData(productData.manufacture) || [],
    expiry: productData.expdt,
    exploreMore: productData.explore_more?.[0],
    showBestSeller: productData.show_bestseller,
    tags: productData.tags || [],
    proFlag: productData.pro_flag,
    returnAvailable: productData.return_available,
    messageOnReturn: productData.message_on_return,
    featured: productData.featured,
    onlyWishlistButton: productData.show_wishlist_button,
    imageUrl: productData.image_url,
    isBackorder: !!productData.is_backorder,
    qnaCount: productData.qna_count,
    reviewSplitUp: transformRatingReviewData(productData.review_splitup),
    sellerName: productData.seller_name,
    sizeData: transformSizeData(productData.size_data),
    latestReviews: transformTopReview(productData.latest_reviews),
    title: productData.name,
    defaultPid: productData?.default_pid,
    shadeImage: productData?.shadeImage,
    tryItOn: productData?.tryItOn,
    isSearchable: !!productData?.is_searchable,
    prData: transformPriceRevealData(
      productData?.pr_data,
      productData?.sale_template
    ),
    isPrEnabled: productData?.pr_enabled_raw,
    isEarlyAccess: productData?.early_access,
    isFlashSale: productData?.flash_sale,
    brandsPart: productData?.brands_part,
    productTitle: productData?.product_title,
    cohortSaleTemplate: getCohortSaleData(productData?.cohort_sale_template),
    bundleChildSkus: productData?.product_qty_map,
    earlyAccessData:
      transformEarlyAccessData(productData?.early_access_data) || {},
    secondaryTag: secondaryTagUrl,
    secondaryTagV2: secondaryTagV2Url,
  };

  const { variants, defaultPid, id } = transformedProduct;
  let selectedVariantId = id;
  let selectedVariantName = '';
  if (variants && variants.length > 0) {
    if (skuId) {
      let skuIdIndex = variants.findIndex(
        (variant) => variant.childId === skuId
      );
      if (skuIdIndex < 0) {
        skuIdIndex = 0;
      }
      const selectedVariant = variants[skuIdIndex];
      selectedVariantId = selectedVariant.childId;
      selectedVariantName = selectedVariant.variantName;
    } else {
      const [firstVariant] = variants;
      selectedVariantId = defaultPid || firstVariant.childId;
      selectedVariantName = firstVariant.variantName;
    }
  }

  transformedProduct.selectedVariantId = selectedVariantId;
  transformedProduct.selectedVariantName = selectedVariantName;

  return transformedProduct;
};

export const transformDynamicFooterLinks = (data: any) => {
  if (!data) {
    return [];
  }
  return data.TopSearchedCategoriesList;
};
